/**
 * Hook for managing question navigation in results pages
 * Shared between QuizResults and AssessmentResults
 */

import { useState, useMemo } from 'react';
import { Question } from '../types';
import { QuizItem } from '@/store/assessment-store';
import { getQuestionType } from '../utils/questionUtils';

interface UseResultsNavigationProps {
  questions: Question[];
  quiz: QuizItem[];
  quizData?: any;
  result?: { results?: Array<{ questionUid: string; result: boolean; correctAnswer: string | string[] | boolean }> };
  /** When provided with onQuestionIndexChange, question index is controlled by parent (e.g. for sidebar sync) */
  controlledQuestionIndex?: number;
  onQuestionIndexChange?: (index: number) => void;
}

export function useResultsNavigation({
  questions,
  quiz,
  quizData,
  result,
  controlledQuestionIndex,
  onQuestionIndexChange,
}: UseResultsNavigationProps) {
  const [internalIndex, setInternalIndex] = useState(0);
  const isControlled = controlledQuestionIndex !== undefined && onQuestionIndexChange != null;
  const questionIndex = isControlled ? controlledQuestionIndex : internalIndex;
  const setQuestionIndex = isControlled ? onQuestionIndexChange : setInternalIndex;

  // Get current question
  const currentQuestion = questions[questionIndex] || null;
  const questionType = currentQuestion ? getQuestionType(currentQuestion) : null;

  // Calculate correct answers for current question (for showing correct answer if wrong)
  // Prefer API result.results when available (from quiz submit response)
  const correctAnswers = useMemo(() => {
    if (!currentQuestion) {
      return null;
    }

    // Use API results only (no fallback to CMS parsing)
    const apiResults = result?.results;
    if (apiResults?.length) {
      const apiResult = apiResults.find(
        (r: { questionUid?: string; question_uid?: string }) =>
          (r.questionUid ?? r.question_uid) === currentQuestion.metadata_uid
      );
      if (apiResult && !apiResult.result) {
        const correct =
          (apiResult as { correctAnswer?: string | string[] | boolean }).correctAnswer ??
          (apiResult as { correct_answer?: string | string[] | boolean }).correct_answer;
        if (correct !== undefined && correct !== null) {
          if (Array.isArray(correct)) {
            return correct.join(', ');
          }
          return typeof correct === 'boolean' ? String(correct) : correct;
        }
      }
    }
    return null;
  }, [currentQuestion, questionIndex, result?.results]);

  return {
    questionIndex,
    setQuestionIndex,
    currentQuestion,
    questionType,
    correctAnswers,
  };
}
